import * as React from "react";
import { useSelector } from "react-redux";
import _ from "lodash";
import { StyledText } from "../../../../assets/typography/typography";
import { colors } from "../../../../components/colors";
import { SpinnerBlock } from "../../../common/Spinner";
import { useSearchParamsManipulation } from "../../../../hooks/use-searchparams-manipulation";
import { SelectField } from "../../../../components/forms/fields/SelectField";
import { DatePickerField } from "../../../../components/forms/fields/DatePickerField";
import { TextField } from "../../../../components/forms/fields/TextField";
import { ButtonPrimary } from "../../../../components/NewButtons";
import { useGetShops } from "../../service/shop-api";
import * as authSelectors from "../../../auth/authSelectors";
import moment from "moment";

const OrderListFilterForm: React.FC = () => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const inputRef = React.useRef<HTMLInputElement>();
  const {
    fromDate,
    toDate,
    shopId,
    searchValue,
    removeFromDate,
    removeToDate,
    removeShopId,
    removeSearchValue,
    setShopId,
    setFromDate,
    setToDate,
    setSearchValue,
  } = useSearchParamsManipulation();

  const { data: shops, isLoading } = useGetShops(organizationKeyName, true);

  const fromDate_ = fromDate
    ? fromDate
    : moment().subtract(1, "month").format();
  const toDate_ = toDate ? toDate : moment().format();

  const shopOptions = React.useMemo(() => {
    const cleared = (shops || []).filter((x) => !!x.id);
    const mapped =
      cleared.map((x) => ({
        label: x.name || x.title || x.id,
        value: x.id,
      })) || [];

    mapped.unshift({ label: "", value: "" });

    return mapped;
  }, [shops]);

  const resetFilter = () => {
    removeSearchValue();
    removeFromDate();
    removeToDate();
    removeShopId();
    // Have to manually clear the input value, as state is kept in URL.
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const setqValue = (value: string) => {
    if (value === "") {
      removeSearchValue();
    } else {
      setSearchValue(value);
    }
  };

  const setFromValue = (value: string) => {
    if (value === "") {
      removeFromDate();
    } else {
      setFromDate(value);
    }
  };

  const setToValue = (value: string) => {
    if (value === "") {
      removeToDate();
    } else {
      setToDate(value);
    }
  };

  const debouncedSetqValue = _.debounce(setqValue, 400);

  const debouncedSetFromValue = _.debounce(setFromValue, 400);

  const debouncedSetToValue = _.debounce(setToValue, 400);

  // Cancelling the debounced functions when the component unmounts
  React.useEffect(() => {
    return () => {
      debouncedSetqValue.cancel();
      debouncedSetFromValue.cancel();
      debouncedSetToValue.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) {
    return <SpinnerBlock />;
  }

  return (
    <div className="card">
      <div className="card-header border-bottom-0 mt-4 justify-content-between">
        <StyledText
          color={colors.textPrimary}
          className="bodyHeaderOneSemi card-title"
        >
          Search in order list
        </StyledText>
      </div>
      <div className="card-body">
        <div className="row">
          <div className="col-md-6 col-lg-4">
            <div className="form-group">
              <SelectField
                label="Restaurant"
                onChange={(value) => {
                  const newValue = value as {
                    value: string;
                    label: string;
                  };
                  if (newValue.value === "") {
                    removeShopId();
                  } else {
                    setShopId(newValue.value);
                  }
                }}
                options={shopOptions}
                value={shopOptions?.find((option) => option.value === shopId)}
              />
            </div>
          </div>
          <div className="col-md-6 col-lg-4">
            <div className="form-group">
              <DatePickerField
                label="From"
                onChange={(value) => {
                  debouncedSetFromValue.cancel();
                  debouncedSetFromValue(value);
                }}
                maxDate={toDate_ ? new Date(toDate_) : undefined}
                value={fromDate_ ? fromDate_ : ""}
              />
            </div>
          </div>
          <div className="col-md-6 col-lg-4">
            <div className="form-group">
              <DatePickerField
                label="To"
                minDate={fromDate_ ? new Date(fromDate_) : undefined}
                onChange={(value) => {
                  debouncedSetToValue.cancel();
                  debouncedSetToValue(value);
                }}
                value={toDate_ ? toDate_ : ""}
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-6 col-lg-12">
            <div className="form-group">
              <TextField
                defaultValue={searchValue || ""}
                label="Search by customer name, e-mail or order id"
                placeholder="Start typing to search..."
                innerRef={inputRef as any}
                onChange={(e) => {
                  debouncedSetqValue.cancel();
                  debouncedSetqValue(e.target.value);
                }}
              />
            </div>
          </div>
        </div>
        <ButtonPrimary onClick={resetFilter}>Reset filter</ButtonPrimary>
      </div>
    </div>
  );
};

export default OrderListFilterForm;
